import type { FC } from 'react'
import { useLocation } from 'react-router'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import {
	ArrowLeftIcon,
	Home03Icon,
	MarkerPin01Icon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/store/store'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'
import { useClaimFinalReviewListing } from '../queries/use-claim-final-review-listing'
import type { FinalReviewListingDetails } from '../types'
import { FinalReviewApprovalDialog } from './final-review-approval-dialog'
import { FinalReviewRejectionDialog } from './final-review-rejection-dialog'
import { FinalScoreBadge } from './final-score-badge'
import { SourceBadge } from './source-badge'

interface FinalReviewDetailHeaderProps {
	listingData: FinalReviewListingDetails
}

type HeaderAction = 'claim' | 'review' | 'viewOnly'

const getHeaderAction = ({
	hasReviewer,
	isAssignedToMe,
	listingStatusRejected
}: {
	hasReviewer: boolean
	isAssignedToMe: boolean
	listingStatusRejected: boolean
}): HeaderAction => {
	if (!hasReviewer) {
		return 'claim'
	}
	if (isAssignedToMe && !listingStatusRejected) {
		return 'review'
	}
	return 'viewOnly'
}

export const FinalReviewDetailHeader: FC<FinalReviewDetailHeaderProps> = ({
	listingData
}) => {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)
	const location = useLocation()
	const fromSearch = location.state?.fromSearch
	const claimMutation = useClaimFinalReviewListing()

	const action = getHeaderAction({
		hasReviewer: !!listingData.assignedReviewerId,
		isAssignedToMe:
			!!listingData.assignedReviewerId &&
			String(listingData.assignedReviewerId) === String(user?.id),
		listingStatusRejected: listingData.reviewStatus === 'failed'
	})
	return (
		<div className='border-neutral-200 border-b px-10 pt-6 pb-6'>
			<div className='flex items-center gap-6'>
				<ButtonLinkLegacy
					className='size-11 shrink-0 px-0!'
					shape='rounded'
					to={{
						pathname:
							routes.LUXE_SUBMISSIONS_FINAL_REVIEW.handle
								.fullPath,
						search: fromSearch
					}}
					variant='icon'
				>
					<ArrowLeftIcon size={20} />
				</ButtonLinkLegacy>

				<div className='flex flex-col gap-2 text-neutral-900'>
					<h1 className='font-bold text-lg'>
						{t('luxe_submissions:final_review_for')}
					</h1>

					<div
						className={cn(
							'flex items-center gap-6 text-neutral-500',
							listingData.isEscalated && 'items-start'
						)}
					>
						<span
							className={cn(
								'flex items-center gap-2 text-sm',
								listingData.isEscalated && 'items-start'
							)}
						>
							<Home03Icon size={20} />
							<span className='flex flex-col'>
								<a
									className={cn(
										'font-medium',
										listingData.listingUrl && 'underline'
									)}
									href={listingData.listingUrl ?? undefined}
									rel='noopener noreferrer'
									target='_blank'
								>
									#
									{listingData.airbnbListingId ??
										listingData.id}
								</a>
								{listingData.isEscalated && (
									<span className='text-danger-600 text-xs'>
										{t('luxe_submissions:escalated')}
									</span>
								)}
							</span>
						</span>

						{listingData.location && (
							<span className='flex items-center gap-2 font-medium text-sm'>
								<MarkerPin01Icon size={20} />
								{listingData.location}
							</span>
						)}

						<SourceBadge source={listingData.source} />

						<FinalScoreBadge
							blockersFailedCount={
								listingData.blockersFailedCount
							}
							designScore={listingData.visualVettingScore}
							finalScore={listingData.finalScore}
							hasBreakdownTooltip={false}
							hasScoreFallback={false}
						/>
					</div>
				</div>

				<div className='ml-auto flex items-center gap-3'>
					{action === 'claim' && (
						<ButtonLegacy
							disabled={claimMutation.isPending}
							onClick={() =>
								claimMutation.mutate([listingData.id])
							}
							type='button'
							variant='tertiary'
						>
							{t('luxe_submissions:claim')}
							{claimMutation.isPending && <Spinner />}
						</ButtonLegacy>
					)}

					{action === 'review' && (
						<>
							<FinalReviewRejectionDialog
								listingId={listingData.id}
								trigger={
									<ButtonLegacy
										type='button'
										variant='outline'
									>
										{t('common:reject')}
									</ButtonLegacy>
								}
							/>
							<FinalReviewApprovalDialog
								isEscalated={listingData.isEscalated}
								listingId={listingData.id}
								trigger={
									<ButtonLegacy type='button' variant='brand'>
										{t(
											'luxe_submissions:approve_for_walkthrough'
										)}
									</ButtonLegacy>
								}
							/>
						</>
					)}

					{action === 'viewOnly' && (
						<span className='font-semibold text-neutral-900 text-sm'>
							{t('luxe_submissions:view_only')}
						</span>
					)}
				</div>
			</div>
		</div>
	)
}
